import { useState, useEffect } from 'react';
import { get, getDatabase, ref } from 'firebase/database';
import { ExternalUser } from '../types/guestAccess';

export interface Userprofile {
    key: string;
    name: string;
    email?: string;
    company?: string;
    admin?: boolean;
    superadmin?: boolean;
    accessToWeb?: boolean;
    groups?: string[];
    lastActive?: string;
    lastAppStart?: string;
    lastDeviceUsed?: {
        identifierForVendor: string;
        model: string;
        name: string;
        systemName: string;
        systemVersion: string;
    };
    createdBy?: string;
    createdAt?: string;
    deleted?: boolean;
    deletedAt?: string;
    deletedBy?: string;
    isExternal?: boolean;
}

/**
 * Map already-loaded user profiles to the ExternalUser shape used by the
 * external-user pickers. External users live in the same users/userList node
 * (isExternal: true) with the node key as their uid, so this avoids a separate
 * externalUserApi fetch when the full user list is already in memory.
 */
export const toExternalUsers = (users: Userprofile[] | undefined): ExternalUser[] =>
    (users || [])
        .filter((u) => u.isExternal && !u.deleted)
        .map((u) => ({
            uid: u.key,
            name: u.name,
            email: u.email || '',
            company: u.company,
            isExternal: true as const,
            createdAt: u.createdAt || '',
            createdBy: u.createdBy || '',
        }));

export const useUsersData = () => {
    const [users, setUsers] = useState<Userprofile[] | undefined>(undefined);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
    const [usersError, setUsersError] = useState<Error | null>(null);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        setUsersError(null);

        try {
            const db = getDatabase();
            const buildingsRef = ref(db, 'users/userList');
            const snapshot = await get(buildingsRef);

            if (snapshot.exists()) {
                const data = snapshot.val() as { [key: string]: Userprofile };
                const userPromises = Object.entries(data).map(async ([key, userData]) => {
                    return {...userData, key} as Userprofile;
                });

                const usersArray = await Promise.all(userPromises);
                setUsers(usersArray);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error('Failed to load users data', err);
            setUsersError(err instanceof Error ? err : new Error('Unknown error'));
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        fetchUsers().then();
    }, []);

    return {
        users,
        loadingUsers,
        usersError,
        refreshUsers: fetchUsers
    };
};